import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  ViewColumn as ViewColumnIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Check as CheckIcon,
  Save as SaveIcon,
  SaveAs as SaveAsIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { useGridConfigs } from './useGridConfigs';

const MENU_BUTTON_STYLE = {
  backgroundColor: '#e1bee7',
  color: 'black',
  '&:hover': { backgroundColor: 'rgba(186, 104, 200, 0.4)' },
};

/**
 * GridConfigMenu - Toolbar dropdown for per-user AG Grid layout persistence.
 *
 * Two usage modes:
 *
 *  - **Controlled**: pass an explicit ``controller`` (the return value of
 *    ``useGridConfigs``). Required when the consumer needs to wire
 *    ``controller.onGridReady`` to ``<PamGrid onGridReady=…/>`` so the
 *    saved default applies even if the grid mounts after configs load.
 *    This is the recommended form for new code.
 *
 *  - **Self-contained**: pass ``api`` + ``app`` + ``gridId`` + ``gridRef``
 *    and the menu instantiates the hook internally. Simpler but the
 *    auto-apply-default only fires if the grid was already ready when
 *    configs arrived (fine for most layouts, racy for huge grids).
 *
 * @param {Object} props
 * @param {Object} [props.controller] - Output of ``useGridConfigs(...)``.
 *   When provided, ``api``/``app``/``gridId``/``gridRef`` are ignored.
 * @param {Object} [props.api] - axios-shaped client (self-contained mode).
 * @param {string} [props.app] - URL slug of the host app (self-contained mode).
 * @param {string} [props.gridId] - Per-app grid id (self-contained mode).
 * @param {Object} [props.gridRef] - React ref to the AG Grid (self-contained mode).
 * @param {Object} [props.sx] - Optional MUI sx override for the trigger Button.
 * @param {string} [props.label='View'] - Trigger button label.
 */
export const GridConfigMenu = ({
  controller,
  api,
  app,
  gridId,
  gridRef,
  sx,
  label = 'View',
}) => {
  // Always call the hook so React's rules-of-hooks are honoured even when
  // the consumer prefers the controlled form. We hand it the same identity
  // but pass no gridApiRef when controlled — the consumer's controller
  // owns the ref binding in that case.
  const internalController = useGridConfigs(
    controller ? null : api,
    controller ? {} : { app, gridId, gridApiRef: gridRef },
  );
  const c = controller || internalController;

  const {
    configs,
    activeName,
    isLoading,
    saveConfig,
    applyConfig,
    deleteConfig,
    setDefault,
  } = c;

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [pendingIsDefault, setPendingIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);

  const closeMenu = useCallback(() => setMenuAnchor(null), []);

  const handleApply = useCallback((name) => {
    applyConfig(name);
    closeMenu();
  }, [applyConfig, closeMenu]);

  const openSaveDialog = useCallback(() => {
    setPendingName(activeName || '');
    setPendingIsDefault(false);
    setSaveDialogOpen(true);
    closeMenu();
  }, [activeName, closeMenu]);

  const handleSaveSubmit = useCallback(async () => {
    if (!pendingName || !pendingName.trim()) return;
    setBusy(true);
    try {
      await saveConfig(pendingName.trim(), { isDefault: pendingIsDefault });
      setSaveDialogOpen(false);
    } finally {
      setBusy(false);
    }
  }, [pendingName, pendingIsDefault, saveConfig]);

  const handleSetDefault = useCallback(async () => {
    if (!activeName) return;
    closeMenu();
    setBusy(true);
    try {
      await setDefault(activeName);
    } finally {
      setBusy(false);
    }
  }, [activeName, setDefault, closeMenu]);

  const handleDelete = useCallback(async () => {
    if (!activeName) return;
    closeMenu();
    setBusy(true);
    try {
      await deleteConfig(activeName);
    } finally {
      setBusy(false);
    }
  }, [activeName, deleteConfig, closeMenu]);

  const triggerLabel = activeName ? `${label}: ${activeName}` : label;

  return (
    <>
      <Tooltip title="Saved column layouts">
        <span>
          <Button
            sx={{ ...MENU_BUTTON_STYLE, ...(sx || {}) }}
            startIcon={busy || isLoading ? <CircularProgress size={16} /> : <ViewColumnIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            disabled={busy}
          >
            {triggerLabel}
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        {configs.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No saved layouts" />
          </MenuItem>
        )}
        {configs.map((cfg) => (
          <MenuItem key={cfg.name} onClick={() => handleApply(cfg.name)}>
            <ListItemIcon>
              {cfg.name === activeName ? <CheckIcon fontSize="small" /> : <span style={{ width: 20 }} />}
            </ListItemIcon>
            <ListItemText
              primary={cfg.name}
              secondary={cfg.isDefault ? 'default' : null}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={openSaveDialog}>
          <ListItemIcon><SaveAsIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Save current view as…" />
        </MenuItem>
        <MenuItem onClick={handleSetDefault} disabled={!activeName}>
          <ListItemIcon><StarIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Set as default" />
        </MenuItem>
        <MenuItem onClick={handleDelete} disabled={!activeName}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save current layout</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1, minWidth: 320 }}>
            <TextField
              autoFocus
              label="Layout name"
              value={pendingName}
              onChange={(e) => setPendingName(e.target.value)}
              size="small"
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={pendingIsDefault}
                  onChange={(e) => setPendingIsDefault(e.target.checked)}
                />
              }
              label="Set as default"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={handleSaveSubmit}
            startIcon={busy ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={busy || !pendingName.trim()}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GridConfigMenu;
